import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Season } from '@shared/models/seasons';
import { PayableOption } from '@shared/models/payable-option';
import { ProductSubscription } from '@shared/models/product-subscription';
import { removeErrorFromControls } from '@core/functions/remove-error-from-controls';
import { isValidDate } from '@core/functions/is-valid-date';
import { PayableOptionId, ProductPayableOptionApiService } from '@bcmApiServices/product-payable-option.api-service';
import {
    addDays,
    addHours,
    addMonths,
    addWeeks,
    addYears,
    endOfMonth,
    isFirstDayOfMonth,
    startOfToday,
    startOfYear,
    subMinutes,
} from '@core/date.facade';
import { SeasonApiService } from '@bcmApiServices/seasons.api-service';
import { U2bDateValidators } from '@shared/validators/date/date-validators';

@Component({
    selector: 'form-widget-subscription-period',
    templateUrl: './form-widget-subscription-period.component.html',
    styleUrls: ['./form-widget-subscription-period.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormWidgetSubscriptionPeriodComponent implements OnInit, OnDestroy {

    @Input()
    parentFormGroup: UntypedFormGroup;

    subscriptionPeriodFormGroup: UntypedFormGroup;

    seasonFormControl = new FormControl<Season>(null);

    payableOptions: PayableOption[];
    PayableOptionId = PayableOptionId;
    seasons: Season[];

    private _unsubscribeAll = new Subject<any>();

    constructor(private _formBuilder: UntypedFormBuilder,
                private _productPayableOptionApiService: ProductPayableOptionApiService,
                private _seasonApiService: SeasonApiService) {
    }

    ngOnInit(): void {
        this.subscriptionPeriodFormGroup = this._createForm();

        this._loadPayableOptions();
        this._loadSeasons();

        setTimeout(() => {
            this.parentFormGroup.addControl('subscriptionPeriodForm', this.subscriptionPeriodFormGroup);
        });

        this.seasonFormControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(season => {
                this.subscriptionPeriodFormGroup.patchValue({
                    vestingPeriodFrom: season?.startDate || null,
                    vestingPeriodUntil: season?.endDate || null
                });
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    comparePayableOptions(payableOption1: PayableOption, payableOption2: PayableOption): boolean {
        return payableOption1?.id === payableOption2?.id;
    }

    compareSeasons(season1: Season, season2: Season): boolean {
        return season1?.id === season2?.id;
    }

    private _createForm(productSubscription?: ProductSubscription): UntypedFormGroup {
        const minFromDate = startOfToday();
        const minVestingPeriodFrom = startOfYear(minFromDate);
        const form = this._formBuilder.group({
            payableOption: [productSubscription?.payableOption || null, [U2bValidators.required('Bitte Zahlungsintervall auswählen')]],
            fromDate: [
                productSubscription?.fromDate || null,
                [U2bValidators.required('Bitte angeben, zu welchem Datum das Abo starten soll.')]
            ],
            toDate: [productSubscription?.toDate || null],
            vestingPeriodFrom: [
                productSubscription?.vestingPeriodFrom || null,
                [U2bValidators.required('Bitte gib den Leistungszeitraum an.')]
            ],
            vestingPeriodUntil: [
                {value: productSubscription?.vestingPeriodUntil || null, disabled: productSubscription?.lastDayOfMonth},
                [U2bValidators.required('Bitte gib den Leistungszeitraum an.')]
            ],
            lastDayOfMonth: [productSubscription?.lastDayOfMonth || false],
        });

        const payableOptionField = form.get('payableOption');
        const vestingPeriodFromField = form.get('vestingPeriodFrom');
        const vestingPeriodUntilField = form.get('vestingPeriodUntil');
        const lastDayOfMonthField = form.get('lastDayOfMonth');

        form.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                if (!value) {
                    return;
                }

                const {
                    fromDate,
                    toDate,
                    vestingPeriodFrom,
                    vestingPeriodUntil,
                } = value;

                if (fromDate && toDate) {
                    if (fromDate > toDate) {
                        form.get('fromDate').setErrors({
                            wrongDateRange: {
                                message: 'Das "von" Datum darf nicht hinter dem "bis" Datum liegen'
                            }
                        });
                        form.get('toDate').setErrors({
                            wrongDateRange: {
                                message: 'Das "bis" Datum darf nicht vor dem "von" Datum liegen'
                            }
                        });
                    } else {
                        removeErrorFromControls('wrongDateRange', [
                            form.get('fromDate'),
                            form.get('toDate'),
                        ]);
                    }
                }

                if (vestingPeriodFrom && vestingPeriodUntil) {
                    if (vestingPeriodFrom > vestingPeriodUntil) {
                        vestingPeriodFromField.setErrors({
                            wrongDateRange: {
                                message: 'Das "Laufzeit Start" Datum darf nicht hinter dem "Laufzeit Ende" Datum liegen'
                            }
                        });
                        vestingPeriodUntilField.setErrors({
                            wrongDateRange: {
                                message: 'Das "Laufzeit Ende" Datum darf nicht vor dem "Laufzeit Start" Datum liegen'
                            }
                        });
                    } else {
                        removeErrorFromControls('wrongDateRange', [
                            vestingPeriodFromField,
                            vestingPeriodUntilField,
                        ]);
                    }
                }

            });

        vestingPeriodFromField
            .valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                if (isValidDate(value) && payableOptionField.value && vestingPeriodUntilField.value == null) {
                    let newDate: Date;
                    let setUntilDateToLastDayOfMonth = false;
                    const firstDayOfMonth = isFirstDayOfMonth(value);

                    switch (payableOptionField.value.id) {
                        case PayableOptionId.Yearly:
                            newDate = addYears(value, 1);
                            break;
                        case PayableOptionId.HalfYearly:
                            newDate = addMonths(value, 6);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.QuarterYearly:
                            newDate = addMonths(value, 3);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.Monthly:
                            newDate = addMonths(value, 1);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.Weekly:
                            newDate = addWeeks(value, 1);
                            break;
                        case PayableOptionId.Daily:
                            newDate = addDays(value, 1);
                            break;
                        case PayableOptionId.Hourly:
                            newDate = addHours(value, 1);
                            break;
                    }

                    vestingPeriodUntilField.patchValue(subMinutes(newDate, 1));

                    if (firstDayOfMonth && setUntilDateToLastDayOfMonth) {
                        lastDayOfMonthField.setValue(true);
                    }
                }
            });

        lastDayOfMonthField
            .valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                if (value) {
                    form.get('vestingPeriodUntil').disable();
                    form.get('vestingPeriodUntil').patchValue(endOfMonth(form.get('vestingPeriodUntil').value || form.get('vestingPeriodFrom').value));
                } else {
                    form.get('vestingPeriodUntil').enable();
                }
            });

        return form;
    }

    private _loadPayableOptions(): void {
        this._productPayableOptionApiService
            .getAll()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((payableOptions: PayableOption[]) => {
                this.payableOptions = payableOptions.filter((po => po.id < 100));
            });
    }

    private _loadSeasons(): void {
        this._seasonApiService
            .getAllSeasons()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((seasons: Season[]) => {
                this.seasons = seasons;
            });
    }

}
